import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import type { SubmitHandler } from 'react-hook-form';

import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { NoopT } from 'declarations/types/common';
import type { SelectOptionT } from 'declarations/types/select';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useFetchClaimStatuses } from 'hooks/api/useClaimStatuses';
import { useChangeClaimStatus } from 'hooks/api/useTickets';

import useTicketDetailsPopup from '../../../hooks/useTicketDetailsPopup';
import type { ChangeTicketClaimStatusFormValuesT } from '../ChangeTicketClaimStatusPopup';

type UseChangeTicketClaimStatusPopupT = {
  isChangingClaimStatus: boolean;
  isClaimStatusChanged: boolean;
  isClaimStatusChangeError: boolean;
  claimStatusesOptions: SelectOptionT[];
  isLoadingClaimStatusesOptions: boolean;
  changeTicketClaimStatusErrorMessage: string | undefined;
  changeTicketClaimStatusValidationErrorMessage: string | undefined;
  showPopupContent: boolean;
  handleChangeTicketClaimStatus: SubmitHandler<ChangeTicketClaimStatusFormValuesT>;
  handleClosePopup: (onClosePopupCb: NoopT) => NoopT;
};

const useChangeTicketClaimStatusPopup = (): UseChangeTicketClaimStatusPopupT => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();
  const { ticketDetails } = useTicketDetailsPopup();
  const claimStatusesRequestParams = {
    isEnabled: true,
    roofTopId: ticketDetails?.roofTop.id || '',
  };

  /**
   * Api hooks
   */
  const claimStatusesResult = useFetchClaimStatuses(claimStatusesRequestParams);

  const changeClaimStatusMutation = useChangeClaimStatus({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
    },
  });

  /**
   * Variables
   */
  const claimStatusesOptions = useMemo(
    () =>
      claimStatusesResult.data
        ? claimStatusesResult.data?.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [claimStatusesResult.data]
  );

  const changeTicketClaimStatusErrorMessage = useMemo(
    () => getErrorMessage(changeClaimStatusMutation.error),
    [changeClaimStatusMutation.error]
  );

  const changeTicketClaimStatusValidationErrorMessage = useMemo(
    () => getValidationErrorMessage(changeClaimStatusMutation.error),
    [changeClaimStatusMutation.error]
  );

  /**
   * Handlers
   */
  const resetChangeTicketClaimStatusMutationState = useCallback(() => {
    if (changeClaimStatusMutation.isSuccess || changeClaimStatusMutation.isError) {
      changeClaimStatusMutation.reset();
    }
  }, [changeClaimStatusMutation]);

  const handleClosePopup = useCallback(
    (onClosePopupCb: NoopT) => () => {
      onClosePopupCb();
      resetChangeTicketClaimStatusMutationState();
    },
    [resetChangeTicketClaimStatusMutationState]
  );

  const handleChangeTicketClaimStatus = useCallback(
    (data: ChangeTicketClaimStatusFormValuesT) => {
      changeClaimStatusMutation.mutate(data);
    },
    [changeClaimStatusMutation]
  );

  return {
    isChangingClaimStatus: changeClaimStatusMutation.isLoading,
    isClaimStatusChanged: changeClaimStatusMutation.isSuccess,
    isClaimStatusChangeError: changeClaimStatusMutation.isError,
    claimStatusesOptions,
    isLoadingClaimStatusesOptions: claimStatusesResult.isLoading,
    changeTicketClaimStatusErrorMessage,
    changeTicketClaimStatusValidationErrorMessage,
    showPopupContent: changeClaimStatusMutation.isIdle || changeClaimStatusMutation.isLoading,
    handleChangeTicketClaimStatus,
    handleClosePopup,
  };
};

export default useChangeTicketClaimStatusPopup;
